import React, { useEffect, useMemo, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import _ from 'underscore';
import { useAngularService } from 'helpers/get_angular_service';
import { useExtendState } from 'helpers/use_extend_state';
import humanize from 'helpers/humanize';
import ExportButton from '../../components/export_button';
import ToggleTableFiltersButton from '../../components/toggle_table_filters_button';
import MultipleSelect from '../../components/multiple_select';
import { MembershipPlan } from '../../types/membership_plan';
import Select from '../../components/select';
import Switch from '../../components/switch';
import { useFiltersOpen } from 'helpers/use_filters_open';
import Input from '../../components/input';
import useGym from 'helpers/use_gym';
import usePaymentMethods from 'helpers/use_payment_methods';

interface InvoicesHeaderProps {
  filters: any;
  setFilters: (filters: any, params: undefined, skipStorage: boolean) => void;
  getCsv: () => void;
  isContactPage: () => boolean;
  paymentStatuses: string[];
  membershipPlans: MembershipPlan[];
  locations: any[];
  membershipPlanCategories: any[];
  sources: any[];
  showFilterRow?: boolean;
}

type RefundFilter = 'include' | 'exclude' | 'refunds_only';

const refundsOptions = [
  { value: 'include', label: 'Include' },
  { value: 'exclude', label: 'Exclude' },
  { value: 'refunds_only', label: 'Refunds only' },
];

const getRefundsFilters = (refunds: RefundFilter) => {
  if (refunds === 'include') {
    return {
      refunds_only: false,
      exclude_refunds: false,
    };
  }
  if (refunds === 'exclude') {
    return {
      refunds_only: false,
      exclude_refunds: true,
    };
  }
  if (refunds === 'refunds_only') {
    return {
      refunds_only: true,
      exclude_refunds: false,
    };
  }
};

const getInitialRefunds = (filters): RefundFilter => {
  if (!filters.refunds_only && !filters.exclude_refunds) {
    return 'include';
  }
  if (!filters.refunds_only && filters.exclude_refunds) {
    return 'exclude';
  }
  if (filters.refunds_only && !filters.exclude_refunds) {
    return 'refunds_only';
  }
};

const InvoicesHeader = ({
  filters: externalFilters,
  setFilters: setExternalFilters,
  getCsv,
  isContactPage,
  paymentStatuses,
  membershipPlans,
  locations,
  membershipPlanCategories,
  sources,
  showFilterRow,
}: InvoicesHeaderProps) => {
  const ReactModal = useAngularService('ReactModal');
  const $rootScope = useAngularService('$rootScope');
  const $stateParams = useAngularService('$stateParams');
  const gym = useGym();

  const [filters, extendFilters, setFilters] = useExtendState<any>(externalFilters);
  const [refunds, setRefunds] = useState<RefundFilter>(getInitialRefunds(filters));
  const filtersOpenKey = useMemo(() => (isContactPage() ? 'contactInvoices' : 'invoices'), [
    isContactPage,
  ]);
  const [filtersOpen, toggleFilters] = useFiltersOpen(filtersOpenKey);
  const [search, setSearch] = useState(filters.search || '');
  const paymentMethods = usePaymentMethods();

  useEffect(() => {
    const unsubscribe = $rootScope.$on('invoices:filtersPresetSelected', (event, param) => {
      setFilters(param);
    });
    return unsubscribe;
  }, [$rootScope, setFilters]);

  useEffect(() => {
    if (filtersOpen) return;
    toggleFilters(!!showFilterRow);
  }, [showFilterRow]);

  const syncSearch = useDebouncedCallback((val) => {
    extendFilters({ search: val });
  }, 400);

  const displayFiltersModal = () => {
    ReactModal.open({
      component: 'InvoicesFiltersModal',
      props: {
        filters: filters,
        contactPage: isContactPage(),
        paymentStatuses: paymentStatuses,
        membershipPlans: membershipPlans,
        locations: locations,
        membershipPlanCategories: membershipPlanCategories,
        sources: sources,
        displayStatus: displayStatus,
        paymentMethodFilterAvailable: isPaymentMethodFilterAvailable(),
        paymentMethods: paymentMethods,
        getInitialRefunds: getInitialRefunds,
        refundsOptions: refundsOptions,
        getRefundsFilters: getRefundsFilters,
      },
    }).then((newFilters) => {
      setFilters(newFilters);
    });
  };

  const displayStatus = (status: string) => {
    if (status === 'chargeback') {
      return 'Failed: Chargeback';
    }

    if (status === 'auto_reschedule') {
      return 'Upcoming (auto-reschedule)';
    }

    return humanize(status);
  };

  useEffect(() => {
    if ($stateParams.status === 'failed') {
      const updatedFilters = { ...filters };

      paymentStatuses.forEach((status) => {
        updatedFilters[status] = status === 'failed';
      });

      extendFilters(updatedFilters);
      toggleFilters(true);
    }
    if ($stateParams.status === 'paid') {
      const updatedFilters = { ...filters };

      paymentStatuses.forEach((status) => {
        updatedFilters[status] = status === 'paid';
      });

      extendFilters(updatedFilters);
      toggleFilters(true);
    }
  }, [$stateParams]);

  const selectedStatuses = useMemo(() => {
    return paymentStatuses.filter((status) => filters[status]);
  }, [filters, paymentStatuses]);

  const prepareFilters = (filters) => {
    return { ...filters, search: filters.search?.length > 2 ? filters.search : '' };
  };

  useEffect(() => {
    const preparedFilters = prepareFilters(filters);
    if (!_.isEqual(preparedFilters, externalFilters)) {
      setExternalFilters(preparedFilters, undefined, !!$stateParams.status);
    }
  }, [filters]);

  const isPaymentMethodFilterAvailable = () => {
    return gym.has_payment_provider && !isContactPage();
  };

  const isRefundsAvailable = () => {
    return gym.refunds_enabled && gym.uses_ezidebit;
  };

  return (
    <>
      <div className="table-header">
        <Input
          value={search}
          onChange={(val) => {
            setSearch(val);
            syncSearch(val);
          }}
          placeholder={
            isContactPage()
              ? 'Search by invoice number'
              : 'Search by contact name or invoice number'
          }
        />

        <ToggleTableFiltersButton
          toggle={() => toggleFilters(!filtersOpen)}
          toggleModal={() => displayFiltersModal()}
          active={filtersOpen}
        />
        <ExportButton onExport={() => getCsv()} variant="table-header" />
      </div>
      {filtersOpen && (
        <div className="table-header-filters">
          {isContactPage() ? (
            <>
              {paymentStatuses.map((status) => (
                <div className="table-header-filters__item" key={status} style={{ minWidth: 80 }}>
                  <label>{displayStatus(status)}</label>
                  <Switch
                    checked={filters[status]}
                    onChange={(val) => {
                      extendFilters({ [status]: val });
                    }}
                  />
                </div>
              ))}
            </>
          ) : (
            <>
              <div className="table-header-filters__item">
                <label>Status</label>
                <MultipleSelect
                  appendToBody
                  value={selectedStatuses}
                  options={paymentStatuses.map((status) => ({
                    value: status,
                    label: displayStatus(status),
                  }))}
                  onChange={(statuses) => {
                    const updatedFilters = { ...filters };
                    paymentStatuses.forEach((status) => {
                      updatedFilters[status] = !statuses.length || statuses.indexOf(status) >= 0;
                    });
                    extendFilters(updatedFilters);
                  }}
                  alignLeft
                  minWidth={175}
                  emptySelectsAll
                />
              </div>

              <div className="table-header-filters__item">
                <label>Membership</label>
                <MultipleSelect
                  appendToBody
                  value={filters.membership_plan_ids}
                  options={membershipPlans}
                  onChange={(membership_plan_ids) => extendFilters({ membership_plan_ids })}
                  minWidth={175}
                  emptySelectsAll
                />
              </div>

              <div className="table-header-filters__item">
                <label>Location</label>
                <MultipleSelect
                  appendToBody
                  value={filters.location_ids}
                  options={locations}
                  onChange={(location_ids) => extendFilters({ location_ids })}
                  minWidth={175}
                  emptySelectsAll
                />
              </div>

              <div className="table-header-filters__item">
                <label>Category</label>
                <MultipleSelect
                  appendToBody
                  value={filters.category_ids}
                  options={membershipPlanCategories}
                  onChange={(category_ids) => extendFilters({ category_ids })}
                  minWidth={175}
                  emptySelectsAll
                />
              </div>

              {isPaymentMethodFilterAvailable() && (
                <div className="table-header-filters__item">
                  <label>Payment Method</label>
                  <Select
                    value={filters.payment_method}
                    options={paymentMethods}
                    onChange={(payment_method) => {
                      extendFilters({ payment_method });
                    }}
                    emptyLabel="Show all"
                    allowEmptyOption
                    variant="v2"
                    width={175}
                  />
                </div>
              )}

              <div className="table-header-filters__item">
                <label>Added From</label>
                <MultipleSelect
                  appendToBody
                  value={filters.sources}
                  options={sources}
                  onChange={(sources) => extendFilters({ sources })}
                  minWidth={175}
                  emptySelectsAll
                />
              </div>

              {isRefundsAvailable() && (
                <div className="table-header-filters__item">
                  <label>Refunds</label>
                  <Select
                    value={refunds}
                    options={refundsOptions}
                    onChange={(refunds: RefundFilter) => {
                      const refundsFilters = getRefundsFilters(refunds);
                      setRefunds(refunds);
                      extendFilters(refundsFilters);
                    }}
                    variant="v2"
                    width={175}
                  />
                </div>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
};

angular.module('gym').value('InvoicesHeader', InvoicesHeader);
